import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PersonIcon from '@mui/icons-material/Person';

interface DayAvailability {
  date: string;
  day_of_week: string;
  is_closed: boolean;
  availability: {
    max_capacity: number;
    available_spots: number;
    utilization_percent: number;
    is_full: boolean;
    sessions: {
      [key: string]: {
        available_spots: number;
        max_capacity: number;
        start_time: string;
        end_time: string;
      };
    };
  };
  my_bookings: any[];
}

interface MemberCapacityCalendarProps {
  calendarData: DayAvailability[];
  loading: boolean;
  error: string | null;
  onDateClick: (date: string) => void;
  isMonthView?: boolean;
}

const MemberCapacityCalendar: React.FC<MemberCapacityCalendarProps> = ({
  calendarData,
  loading,
  error,
  onDateClick,
  isMonthView = false,
}) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md')); // >= 960px (desktop - 7 columns)

  // Helper to get color based on availability
  const getAvailabilityColor = (availableSpots: number, maxCapacity: number): string => {
    if (maxCapacity === 0) return 'grey';
    const percentAvailable = (availableSpots / maxCapacity) * 100;
    if (percentAvailable > 50) return '#4caf50'; // Green
    if (percentAvailable > 20) return '#ff9800'; // Orange
    if (percentAvailable > 0) return '#f44336'; // Red
    return '#9e9e9e'; // Grey (full)
  };

  // Helper to get availability label
  const getAvailabilityLabel = (availableSpots: number, maxCapacity: number): string => {
    if (maxCapacity === 0) return 'Closed';
    if (availableSpots === 0) return 'Full';
    if (availableSpots > 10) return 'Good availability';
    if (availableSpots > 5) return 'Limited';
    return 'Few spots left';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (calendarData.length === 0) {
    return (
      <Alert severity="info">
        No availability data found for the selected date range.
      </Alert>
    );
  }

  // For month view, calculate the starting day of week (0 = Sunday, 6 = Saturday)
  const getStartingDayOfWeek = (): number => {
    if (!isMonthView || calendarData.length === 0) return 0;
    const [year, month, day] = calendarData[0].date.split('-').map(Number);
    const firstDate = new Date(year, month - 1, day);
    return firstDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
  };

  const startingDayOfWeek = getStartingDayOfWeek();
  const emptySlots = isMonthView ? startingDayOfWeek : 0;

  return (
    <Box>
      <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 7 }}>
        {/* Add empty slots for month view to align with Sun-Sat calendar */}
        {/* Only show empty slots on desktop (md+) where 7-column calendar layout is used */}
        {isMonthView && isMdUp && Array.from({ length: emptySlots }).map((_, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 1 }} key={`empty-${index}`}>
            <Box sx={{ height: '100%', minHeight: 200 }} />
          </Grid>
        ))}

        {calendarData.map((day) => {
          const hasMyBookings = day.my_bookings && day.my_bookings.length > 0;
          const availabilityColor = getAvailabilityColor(
            day.availability.available_spots,
            day.availability.max_capacity
          );

          // Check if date is in the past
          const [year, month, dayNum] = day.date.split('-').map(Number);
          const cardDate = new Date(year, month - 1, dayNum);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isPast = cardDate < today;

          return (
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 1
              }}
              key={day.date}
            >
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: hasMyBookings ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: isPast ? 0.5 : 1,
                  bgcolor: isPast ? '#f5f5f5' : 'background.paper',
                  '&:hover': {
                    boxShadow: 3,
                    transform: 'translateY(-2px)',
                  },
                }}
                onClick={() => onDateClick(day.date)}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Date Header */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {day.day_of_week}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {(() => {
                        // Parse date as local date to avoid timezone issues
                        const [year, month, dayNum] = day.date.split('-').map(Number);
                        const localDate = new Date(year, month - 1, dayNum);
                        return localDate.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        });
                      })()}
                    </Typography>
                  </Box>

                  {/* Session Availability with My Bookings */}
                  <Box sx={{ mb: 1.5 }}>
                    <Stack spacing={0.5}>
                      {/* Morning Session */}
                      {day.availability.sessions?.AM && (
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              Morning:
                            </Typography>
                            <Chip
                              label={`${day.availability.sessions.AM.available_spots}/${day.availability.sessions.AM.max_capacity}`}
                              size="small"
                              color={
                                day.availability.sessions.AM.available_spots === 0
                                  ? 'error'
                                  : day.availability.sessions.AM.available_spots <= 3
                                  ? 'warning'
                                  : 'success'
                              }
                              sx={{ height: 18, fontSize: '0.65rem', minWidth: 40 }}
                            />
                          </Box>
                          {/* My bookings in this session */}
                          {day.my_bookings?.filter((b: any) => b.session === 'AM').length > 0 && (
                            <Stack direction="row" spacing={0.3} sx={{ mt: 0.3, ml: 1 }} flexWrap="wrap" useFlexGap>
                              {day.my_bookings.filter((b: any) => b.session === 'AM').map((booking: any, idx: number) => (
                                <Chip
                                  key={idx}
                                  label={booking.dependent_name || 'Child'}
                                  size="small"
                                  color={booking.status === 'confirmed' ? 'success' : booking.status === 'waitlisted' ? 'info' : 'warning'}
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }}
                                />
                              ))}
                            </Stack>
                          )}
                        </Box>
                      )}
                      {/* Afternoon Session */}
                      {day.availability.sessions?.PM && (
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              Afternoon:
                            </Typography>
                            <Chip
                              label={`${day.availability.sessions.PM.available_spots}/${day.availability.sessions.PM.max_capacity}`}
                              size="small"
                              color={
                                day.availability.sessions.PM.available_spots === 0
                                  ? 'error'
                                  : day.availability.sessions.PM.available_spots <= 3
                                  ? 'warning'
                                  : 'success'
                              }
                              sx={{ height: 18, fontSize: '0.65rem', minWidth: 40 }}
                            />
                          </Box>
                          {/* My bookings in this session */}
                          {day.my_bookings?.filter((b: any) => b.session === 'PM').length > 0 && (
                            <Stack direction="row" spacing={0.3} sx={{ mt: 0.3, ml: 1 }} flexWrap="wrap" useFlexGap>
                              {day.my_bookings.filter((b: any) => b.session === 'PM').map((booking: any, idx: number) => (
                                <Chip
                                  key={idx}
                                  label={booking.dependent_name || 'Child'}
                                  size="small"
                                  color={booking.status === 'confirmed' ? 'success' : booking.status === 'waitlisted' ? 'info' : 'warning'}
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }}
                                />
                              ))}
                            </Stack>
                          )}
                        </Box>
                      )}
                      {/* Evening Session */}
                      {day.availability.sessions?.EVENING && (
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              Evening:
                            </Typography>
                            <Chip
                              label={`${day.availability.sessions.EVENING.available_spots}/${day.availability.sessions.EVENING.max_capacity}`}
                              size="small"
                              color={
                                day.availability.sessions.EVENING.available_spots === 0
                                  ? 'error'
                                  : day.availability.sessions.EVENING.available_spots <= 3
                                  ? 'warning'
                                  : 'success'
                              }
                              sx={{ height: 18, fontSize: '0.65rem', minWidth: 40 }}
                            />
                          </Box>
                          {/* My bookings in this session */}
                          {day.my_bookings?.filter((b: any) => b.session === 'EVENING').length > 0 && (
                            <Stack direction="row" spacing={0.3} sx={{ mt: 0.3, ml: 1 }} flexWrap="wrap" useFlexGap>
                              {day.my_bookings.filter((b: any) => b.session === 'EVENING').map((booking: any, idx: number) => (
                                <Chip
                                  key={idx}
                                  label={booking.dependent_name || 'Child'}
                                  size="small"
                                  color={booking.status === 'confirmed' ? 'success' : booking.status === 'waitlisted' ? 'info' : 'warning'}
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }}
                                />
                              ))}
                            </Stack>
                          )}
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  {/* Closed indicator */}
                  {day.is_closed && (
                    <Chip
                      label="Closed"
                      size="small"
                      sx={{
                        mt: 1,
                        bgcolor: '#f5f5f5',
                        color: '#757575',
                        fontSize: '0.7rem',
                      }}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default MemberCapacityCalendar;
